import React, { useState, useEffect } from 'react';
import { X, User, DollarSign, Calendar, Briefcase, Phone } from 'lucide-react';

const PaymentForm = ({ payment, apiKey, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    worker_name: '',
    worker_phone: '',
    worker_role: 'Photographe',
    amount: '',
    bonus: 0,
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'Cash',
    payment_status: 'pending',
    work_period_start: '',
    work_period_end: '',
    events_worked: 0,
    notes: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const apiUrl = process.env.REACT_APP_BACKEND_URL;

  const roles = ['Photographe', 'Vidéaste', 'Assistant', 'Éditeur', 'Drone Operator'];
  const paymentMethods = ['Cash', 'Mobile Money', 'Virement Bancaire', 'Chèque'];

  useEffect(() => {
    if (payment) {
      setFormData({
        ...payment,
        work_period_start: payment.work_period_start || '',
        work_period_end: payment.work_period_end || ''
      });
    }
  }, [payment]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const url = payment
        ? `${apiUrl}/api/payroll/payments/${payment.id}`
        : `${apiUrl}/api/payroll/payments/`;
      
      const method = payment ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          ...formData,
          amount: parseFloat(formData.amount),
          bonus: parseFloat(formData.bonus || 0),
          events_worked: parseInt(formData.events_worked || 0)
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.detail || 'Erreur lors de l\'enregistrement');
      }

      onSave();
    } catch (err) {
      console.error('Error saving payment:', err);
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalAmount = (parseFloat(formData.amount || 0) + parseFloat(formData.bonus || 0)).toFixed(2);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-amber-600 text-white p-6 flex justify-between items-center">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <DollarSign className="h-6 w-6" />
            {payment ? 'Modifier le Paiement' : 'Nouveau Paiement'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-full transition-colors">
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Worker Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-blue-900 mb-3">
              <User className="inline h-4 w-4 mr-1" />
              Informations du Travailleur
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom Complet *
                </label>
                <input
                  type="text"
                  required
                  minLength={2}
                  value={formData.worker_name}
                  onChange={(e) => setFormData({ ...formData, worker_name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  placeholder="Jean Mukendi"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Phone className="inline h-4 w-4 mr-1" />
                  Téléphone *
                </label>
                <input
                  type="tel"
                  required
                  value={formData.worker_phone}
                  onChange={(e) => setFormData({ ...formData, worker_phone: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  placeholder="+243 999 000 000"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Briefcase className="inline h-4 w-4 mr-1" />
                  Rôle *
                </label>
                <select
                  required
                  value={formData.worker_role}
                  onChange={(e) => setFormData({ ...formData, worker_role: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                >
                  {roles.map(role => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Événements Travaillés
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.events_worked}
                  onChange={(e) => setFormData({ ...formData, events_worked: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  placeholder="3"
                />
              </div>
            </div>
          </div>

          {/* Payment Details */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-900">Détails du Paiement</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Montant de Base (USD) *
                </label>
                <input
                  type="number"
                  required
                  min="0"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  placeholder="500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prime/Bonus (USD)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.bonus}
                  onChange={(e) => setFormData({ ...formData, bonus: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  placeholder="50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="inline h-4 w-4 mr-1" />
                  Date de Paiement *
                </label>
                <input
                  type="date"
                  required
                  value={formData.payment_date}
                  onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Méthode de Paiement
                </label>
                <select
                  value={formData.payment_method}
                  onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                >
                  {paymentMethods.map(method => (
                    <option key={method} value={method}>{method}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Statut
                </label>
                <select
                  value={formData.payment_status}
                  onChange={(e) => setFormData({ ...formData, payment_status: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                >
                  <option value="pending">En attente</option>
                  <option value="completed">Complété</option>
                  <option value="cancelled">Annulé</option>
                </select>
              </div>
            </div>
          </div>

          {/* Work Period */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Période de Travail (optionnel)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date Début
                </label>
                <input
                  type="date"
                  value={formData.work_period_start}
                  onChange={(e) => setFormData({ ...formData, work_period_start: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date Fin
                </label>
                <input
                  type="date"
                  value={formData.work_period_end}
                  onChange={(e) => setFormData({ ...formData, work_period_end: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes (optionnel)
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              rows="3"
              placeholder="Ex: Paiement pour événements octobre 2026..."
            />
          </div>

          {/* Total Preview */}
          {formData.amount && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-700 font-medium">Montant Total à Payer</span>
                <span className="text-3xl font-bold text-green-600">
                  ${totalAmount}
                </span>
              </div>
              {formData.bonus > 0 && (
                <div className="text-sm text-gray-600 mt-1">
                  ${formData.amount} (base) + ${formData.bonus} (bonus)
                </div>
              )}
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className={`flex-1 px-4 py-2 rounded-lg text-white transition-colors ${
                isSubmitting
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-amber-600 hover:bg-amber-700'
              }`}
            >
              {isSubmitting ? 'Enregistrement...' : payment ? 'Modifier' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaymentForm;
